'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { getUniqueArchiveParams } from '@/utils/getUniqueArchiveParams'
import { useCallback, useEffect, useMemo } from 'react'

export const useArchiveUrl = (archiveId: string, defaultFilter?: string) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { paginationId, filterId, sortingId } = getUniqueArchiveParams(archiveId)

  // Get state from URL or use defaults
  const currentPage = searchParams.has(paginationId) ? Number(searchParams.get(paginationId)) : 1
  const currentFilter = searchParams.get(filterId) || defaultFilter || 'all'
  const currentSort = searchParams.get(sortingId) || undefined

  // Create a memoized value representing only the parameters that affect this specific archive
  // This prevents re-renders when other archive parameters change
  const archiveSpecificParams = useMemo(() => {
    return {
      page: currentPage,
      filter: currentFilter,
      sort: currentSort,
    }
  }, [currentPage, currentFilter, currentSort])

  // Update URL params - ONLY updates URL, doesn't trigger fetches directly
  const updateUrlParams = useCallback(
    (params: Record<string, string | null>) => {
      // Create a new URLSearchParams object from the current params
      const newParams = new URLSearchParams(searchParams.toString())

      // Update provided params
      Object.entries(params).forEach(([key, value]) => {
        if (value === null) {
          newParams.delete(key)
        } else {
          newParams.set(key, value)
        }
      })

      // Create the new URL with updated parameters
      const newUrl = `${pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`

      // Navigate to new URL without scrolling
      // Use router.push to ensure React's router system detects the change
      try {
        router.push(newUrl, { scroll: false })
      } catch (error) {
        console.error(`[CollectionArchive] Error updating URL: `, error)
      }
    },
    [pathname, router, searchParams],
  )

  // Helper function to check if URL params have changed from defaults
  const hasCustomParams = useCallback(() => {
    return currentPage !== 1 || currentFilter !== 'all' || currentSort !== undefined
  }, [currentFilter, currentPage, currentSort])

  return {
    currentPage,
    currentFilter,
    currentSort,
    paginationId,
    filterId,
    sortingId,
    updateUrlParams,
    hasCustomParams,
    searchParams,
    archiveSpecificParams,
  }
}
